import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { X, ChevronDown, ChevronRight, Star, Clock, Eye, Plus, Printer, Inbox, FolderPlus, LayoutGrid } from 'lucide-react';
import { useCollectionTree } from '../hooks';
import { CollectionTree } from './CollectionTree';
import { useUIStore } from '@/stores/ui';
import { Button } from '@/components/Button';
import { useModels } from '@/features/models/hooks';

interface SmartCollection {
  id: string;
  name: string;
  icon: React.ComponentType<any>;
  color: string;
  children?: SmartCollection[];
}

const smartCollections: SmartCollection[] = [
  { id: 'favorites', name: 'Favorites', icon: Star, color: '#eab308' }, // Yellow
  {
    id: 'recent',
    name: 'Recent',
    icon: Clock,
    color: '#3b82f6', // Blue
    children: [
      { id: 'recent-viewed', name: 'Viewed', icon: Eye, color: '#8b5cf6' }, // Purple
      { id: 'recent-added', name: 'Added', icon: Plus, color: '#10b981' }, // Green
      { id: 'recent-printed', name: 'Printed', icon: Printer, color: '#f97316' }, // Orange
    ],
  },
  { id: 'uncategorized', name: 'Uncategorized', icon: Inbox, color: '#6b7280' }, // Gray
];

export function CollectionFilterSidebar() {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    collectionsFilterOpen,
    setCollectionsFilterOpen,
    selectedCollectionIds,
    openCollectionSidebar,
  } = useUIStore();

  const { data: collectionTree, isLoading } = useCollectionTree();
  const [expandedSmartCollections, setExpandedSmartCollections] = useState<Set<string>>(
    new Set(['recent'])
  );

  // Fetch counts for "All Models" and "Favorites"
  const { data: allModelsData } = useModels({ page: 1, limit: 1 });
  const { data: favoritesData } = useModels({ page: 1, limit: 1, isFavorite: true });

  const totalModelsCount = allModelsData?.pagination.total || 0;
  const favoritesCount = favoritesData?.pagination.total || 0;

  if (!collectionsFilterOpen) {
    return null;
  }

  const handleSmartCollectionToggle = (id: string) => {
    const newExpanded = new Set(expandedSmartCollections);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedSmartCollections(newExpanded);
  };

  const handleCollectionSelect = (id: string) => {
    const newParams = new URLSearchParams(searchParams);
    const currentCollections = newParams.getAll('collection');

    // Handle "All Models" - clear all collection filters
    if (id === 'all-models') {
      newParams.delete('collection');
      newParams.set('page', '1');
      setSearchParams(newParams);
      return;
    }

    // Single-select behavior: if clicking already selected, clear; otherwise replace
    if (currentCollections.length === 1 && currentCollections[0] === id) {
      newParams.delete('collection');
    } else {
      newParams.delete('collection');
      newParams.append('collection', id);
    }

    // Reset to page 1 when filter changes
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  return (
    <div className="w-80 border border-border bg-background rounded-lg flex-shrink-0 flex flex-col m-4 shadow-sm">
      {/* Header */}
      <div className="px-4 py-2 border-b border-border flex items-center justify-between flex-shrink-0 rounded-t-lg">
        <h2 className="font-semibold text-foreground">Collections</h2>
        <button
          onClick={() => setCollectionsFilterOpen(false)}
          className="p-1 hover:bg-background-subtle rounded transition-colors"
          title="Close sidebar"
        >
          <X className="h-4 w-4 text-foreground-muted" />
        </button>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-6 min-h-0">
        {/* Smart Collections */}
        <div>
          <h3 className="text-xs font-semibold text-foreground-muted uppercase tracking-wide mb-3">
            Quick Filters
          </h3>
          <div className="space-y-1">
            {/* All Models - Special filter that clears collections */}
            <div
              className={`
                flex items-center gap-2 py-2 px-3 rounded-md cursor-pointer
                hover:bg-background-subtle transition-colors
                ${
                  selectedCollectionIds.length === 0
                    ? 'bg-primary/20 text-primary font-semibold'
                    : 'text-foreground'
                }
              `}
              onClick={() => handleCollectionSelect('all-models')}
            >
              {/* Empty space for chevron alignment */}
              <button
                className="p-0.5 hover:bg-background-subtle rounded transition-colors"
                style={{ visibility: 'hidden' }}
              >
                <ChevronRight className="h-4 w-4 text-foreground-muted" />
              </button>

              {/* Icon */}
              <LayoutGrid
                className="h-4 w-4 flex-shrink-0"
                style={{ color: '#3b82f6' }}
              />

              {/* Name */}
              <span className="flex-1 text-sm">All Models</span>

              {/* Count */}
              {totalModelsCount > 0 && (
                <span className="text-xs text-foreground-muted">{totalModelsCount}</span>
              )}
            </div>

            {smartCollections.map((collection) => {
              const isDirectlySelected = selectedCollectionIds.includes(collection.id);
              const hasSelectedChild = collection.children?.some((child) =>
                selectedCollectionIds.includes(child.id)
              );

              // Parent styling
              let parentBgClass = '';
              let parentTextClass = 'text-foreground';

              if (isDirectlySelected) {
                parentBgClass = 'bg-primary/20';
                parentTextClass = 'text-primary font-semibold';
              } else if (hasSelectedChild) {
                parentBgClass = 'bg-primary/5';
                parentTextClass = 'text-primary/60';
              }

              // Get count for this collection
              const collectionCount = collection.id === 'favorites' ? favoritesCount : undefined;

              return (
                <div key={collection.id}>
                  {/* Smart Collection Item */}
                  <div
                    className={`
                      flex items-center gap-2 py-2 px-3 rounded-md cursor-pointer
                      hover:bg-background-subtle transition-colors
                      ${parentBgClass} ${parentTextClass}
                    `}
                    onClick={() => handleCollectionSelect(collection.id)}
                  >
                    {/* Expand/Collapse Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (collection.children) {
                          handleSmartCollectionToggle(collection.id);
                        }
                      }}
                      className="p-0.5 hover:bg-background-subtle rounded transition-colors"
                      style={{ visibility: collection.children ? 'visible' : 'hidden' }}
                    >
                      {expandedSmartCollections.has(collection.id) ? (
                        <ChevronDown className="h-4 w-4 text-foreground-muted" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-foreground-muted" />
                      )}
                    </button>

                    {/* Icon */}
                    <collection.icon
                      className="h-4 w-4 flex-shrink-0"
                      style={{ color: collection.color }}
                    />

                    {/* Name */}
                    <span className="flex-1 text-sm">{collection.name}</span>

                    {/* Count */}
                    {collectionCount !== undefined && collectionCount > 0 && (
                      <span className="text-xs text-foreground-muted">{collectionCount}</span>
                    )}
                  </div>

                  {/* Children (for Recent) */}
                  {collection.children && expandedSmartCollections.has(collection.id) && (
                    <div className="space-y-1 mt-1">
                      {collection.children.map((child) => {
                        const isChildSelected = selectedCollectionIds.includes(child.id);
                        const isDescendant = isDirectlySelected && !isChildSelected;

                        let childBgClass = '';
                        let childTextClass = 'text-foreground';

                        if (isChildSelected) {
                          childBgClass = 'bg-primary/20';
                          childTextClass = 'text-primary font-semibold';
                        } else if (isDescendant) {
                          childBgClass = 'bg-primary/5';
                          childTextClass = 'text-primary/60';
                        }

                        return (
                          <div
                            key={child.id}
                            className={`
                              flex items-center gap-2 py-2 px-3 rounded-md cursor-pointer
                              hover:bg-background-subtle transition-colors
                              ${childBgClass} ${childTextClass}
                            `}
                            style={{ paddingLeft: '1.5rem' }}
                            onClick={() => handleCollectionSelect(child.id)}
                          >
                            {/* Empty space for chevron alignment */}
                            <div className="w-5" />

                            <child.icon
                              className="h-4 w-4 flex-shrink-0"
                              style={{ color: child.color }}
                            />
                            <span className="flex-1 text-sm">{child.name}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* User Collections */}
        <div>
          <h3 className="text-xs font-semibold text-foreground-muted uppercase tracking-wide mb-3">
            My Collections
          </h3>
          <CollectionTree
            collections={collectionTree || []}
            isLoading={isLoading}
            selectedCollectionIds={selectedCollectionIds}
            onSelect={handleCollectionSelect}
            showCheckbox={false}
            emptyMessage="Create your first collection to organize models"
          />
        </div>
      </div>

      {/* Footer with Add Category Button */}
      <div className="px-4 py-2 border-t border-border flex-shrink-0 rounded-b-lg">
        <Button
          variant="primary"
          size="sm"
          leftIcon={<FolderPlus className="h-4 w-4" />}
          onClick={() => openCollectionSidebar()}
          className="w-full"
        >
          Add Category
        </Button>
      </div>
    </div>
  );
}
